import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import SectionCard from '@/components/common/SectionCard/SectionCard';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';
import { User } from '@/hooks/identity/responseModels/user';
import { UserRole } from '@/config/roles.config';
import { isContractOneEvoMigrated } from '@/utils/oneEvoMigration.utils';
import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import { NumberUtils } from '@/utils/numbers.utils';
import useGetContractTermsAndConditions from './hooks/useGetContractTermsAndConditions';
import useUpdateContractTermsAndConditions from './hooks/useUpdateContractTermsAndConditions';
import ContractTCsForm from './components/ContractTCsForm';
import {
  SpecialContractTypeEnum, NoticePeriodTypeEnum, ContractStatusEnum
} from '@/graphql/contracts/_generated_/contracts';

export interface ContractTCsFormValues {
  specialContractType: SpecialContractTypeEnum | null;
  fixedTermReviewDate: Date | null;
  fixedTermAdditionalInfo: string;
  breakClauseEnabled: boolean;
  breakClauseReviewDate: Date | null;
  breakClauseAdditionalInfo: string;
  noticePeriodType: NoticePeriodTypeEnum | null;
  cancellationAdditionalInfo: string;
}

const getInitialValues = (): ContractTCsFormValues => ({
  specialContractType: null,
  fixedTermReviewDate: null,
  fixedTermAdditionalInfo: '',
  breakClauseEnabled: false,
  breakClauseReviewDate: null,
  breakClauseAdditionalInfo: '',
  noticePeriodType: NoticePeriodTypeEnum.Standard,
  cancellationAdditionalInfo: ''
});

export default function ContractTCsContainer() {
  const sectionCardRef = useRef<any>();
  const { contractId }: { contractId: UUID } = useParams();
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  const { contract } = useGetContractDetails(contractId);
  const { termsAndConditions, isLoading } = useGetContractTermsAndConditions(contractId);
  const { updateContractTermsAndConditions, isLoadingUpdate } = useUpdateContractTermsAndConditions(contractId);
  const { searchUsers } = useSearchUsers();
  const [updatedByUsers, setUpdatedByUsers] = useState<Record<string, User>>({});

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getRole();
  }, [getUserRole]);

  useEffect(() => {
    if (!termsAndConditions) return;

    const userIds = [
      termsAndConditions.fixedTermUpdatedBy,
      termsAndConditions.breakClauseUpdatedBy,
      termsAndConditions.cancellationUpdatedBy
    ].filter((id): id is string => !!id);

    const uniqueIds = [...new Set(userIds)] as UUID[];
    if (uniqueIds.length === 0) return;

    const fetchUsers = async () => {
      const users = await searchUsers({ ids: uniqueIds });
      if (users) {
        const usersMap: Record<string, User> = {};
        users.forEach((user) => {
          usersMap[user.Id] = user;
        });
        setUpdatedByUsers(usersMap);
      }
    };

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    termsAndConditions?.fixedTermUpdatedBy,
    termsAndConditions?.breakClauseUpdatedBy,
    termsAndConditions?.cancellationUpdatedBy
  ]);

  const contractStatus = contract?.status?.status as ContractStatusEnum | undefined;
  const userAllowedStatuses: ContractStatusEnum[] = [
    ContractStatusEnum.Active,
    ContractStatusEnum.ActiveDNR,
    ContractStatusEnum.Terminating
  ];
  const isUserRoleEditable = userRole === UserRole.USER &&
    contractStatus !== undefined && userAllowedStatuses.includes(contractStatus);
  const isEditable = !isContractOneEvoMigrated(contract) &&
    (userRole === UserRole.ADMIN || userRole === UserRole.FULL_ACCESS || isUserRoleEditable);

  const getUserDisplayName = (userId: string | null | undefined): string | null => {
    if (!userId) return null;
    const user = updatedByUsers[userId];
    return user ? `${user.FirstName} ${user.LastName}` : null;
  };

  const formValues: ContractTCsFormValues = termsAndConditions
    ? {
      specialContractType:
        (Object.values(SpecialContractTypeEnum) as string[]).includes(termsAndConditions.specialContractType)
          ? termsAndConditions.specialContractType as SpecialContractTypeEnum
          : null,
      fixedTermReviewDate: termsAndConditions.fixedTermReviewDate
        ? new Date(termsAndConditions.fixedTermReviewDate)
        : null,
      fixedTermAdditionalInfo: termsAndConditions.fixedTermAdditionalInfo ?? '',
      breakClauseEnabled: termsAndConditions.breakClauseEnabled,
      breakClauseReviewDate: termsAndConditions.breakClauseReviewDate
        ? new Date(termsAndConditions.breakClauseReviewDate)
        : null,
      breakClauseAdditionalInfo: termsAndConditions.breakClauseAdditionalInfo ?? '',
      noticePeriodType:
        (Object.values(NoticePeriodTypeEnum) as string[]).includes(termsAndConditions.noticePeriodType)
          ? termsAndConditions.noticePeriodType as NoticePeriodTypeEnum
          : null,
      cancellationAdditionalInfo: termsAndConditions.cancellationAdditionalInfo ?? ''
    }
    : getInitialValues();

  const handleSubmit = (values: ContractTCsFormValues) => {
    updateContractTermsAndConditions({
      specialContractType: values.specialContractType ?? SpecialContractTypeEnum.None,
      fixedTermReviewDate: values.fixedTermReviewDate?.toISOString() ?? null,
      fixedTermAdditionalInfo: values.fixedTermAdditionalInfo || null,
      breakClauseEnabled: values.breakClauseEnabled,
      breakClauseReviewDate: values.breakClauseReviewDate?.toISOString() ?? null,
      breakClauseAdditionalInfo: values.breakClauseAdditionalInfo || null,
      noticePeriodType: values.noticePeriodType ?? NoticePeriodTypeEnum.Standard,
      cancellationAdditionalInfo: values.cancellationAdditionalInfo || null
    });
  };

  return (
    <div className="mb-20">
      <SectionCard
        ref={sectionCardRef}
        title={{
          titleText: 'Contract T&Cs',
        }}
        padding={true}
      >
        <ContractTCsForm
          initialValues={formValues}
          isLoading={isLoading}
          isLoadingUpdate={isLoadingUpdate}
          isEditable={isEditable}
          onSubmit={handleSubmit}
          customerName={contract?.customer.customerName ?? ''}
          customerCode={contract?.customer.customerAccountNumber ?? ''}
          contractNumber={contract?.contractNumber ?? ''}
          startDate={contract?.contractStartDate ?? ''}
          endDate={contract?.contractEndDate ?? ''}
          totalValue={contract
            ? `${contract.currency.currency} ${NumberUtils.prettifyWithRound(contract.totalContractValue)}`
            : ''}
          fixedTermUpdatedBy={getUserDisplayName(termsAndConditions?.fixedTermUpdatedBy)}
          fixedTermUpdatedDate={termsAndConditions?.fixedTermUpdatedDate ?? null}
          breakClauseUpdatedBy={getUserDisplayName(termsAndConditions?.breakClauseUpdatedBy)}
          breakClauseUpdatedDate={termsAndConditions?.breakClauseUpdatedDate ?? null}
          cancellationUpdatedBy={getUserDisplayName(termsAndConditions?.cancellationUpdatedBy)}
          cancellationUpdatedDate={termsAndConditions?.cancellationUpdatedDate ?? null}
        />
      </SectionCard>
    </div>
  );
}
